<template>
  <tr>
    <td class="px-1 py-2" style="width: 50px">
      <figure class="table-figure" style="width: 50px; height: 40px">
        <img class="border rounded" :src="getImage()" height="40" />
      </figure>
    </td>

    <td class="px-1 py-2">
      <div>
        <strong>{{ getProductName() }}</strong> <br />
        {{ getVariantName() }}
      </div>
    </td>
    <td class="px-1 py-2">{{ getProducerName }}</td>

    <td class="px-1 py-2">
      <div>
        <div>
          <strong>{{ getPrice() }}</strong>
        </div>
        <div class="ui-type-caption text-gray-600">
          {{ getPriceInfo() }}
        </div>
      </div>
    </td>

    <td class="px-1 py-2" style="width: 200px">
      <div class="w-100">
        <form action="" @submit.prevent="onSubmit" class="d-flex w-100">
          <CInput
            class="mb-0 mr-1 w-100"
            v-model="quantity"
            type="number"
            min="0"
            :append="suffix()"
            :step="step()"
            :max="
              availability() &&
              (availability().availability_xd
                ? availability().availability_start
                : availability().availability)
            "
            @update:value="onChange"
          />
          <CButton color="gray-300" type="submit" class="mr-1">
            <font-awesome-icon icon="sync" />
          </CButton>
        </form>
        <small
          class="badge badge-gray"
          v-if="availability() && availability().availability_xd"
        >
          Max {{ availability().availability_start }}
          {{ $t("models.cart.quantity.perDelivery") }}
        </small>
        <small
          class="badge badge-gray"
          v-if="availability() && !availability().availability_xd"
        >
          {{ $t("models.cart.quantity.left") }}
          {{ availability().availability }} {{ $t("models.cart.quantity.of") }}
          {{ availability().availability_start }}
        </small>

        <CModal
          :title="$t('family.market.cart.remove.title')"
          size="lg"
          :centered="true"
          :show.sync="showModal"
        >
          <div>
            <h4>{{ getProductName() }}</h4>
            <h5>{{ getVariantName() }}</h5>
            {{ $t("family.market.cart.remove.text") }}
          </div>
          <template #footer>
            <CButton
              color="primary"
              variant="outline"
              @click.prevent="showModal = false"
            >
              {{ $t("common.editor.cancel") }}
            </CButton>
            <CButton color="primary" @click.prevent="onConfirm()">
              {{ $t("common.editor.button.confirm") }}
            </CButton>
          </template>
        </CModal>
      </div>
    </td>

    <td class="px-1 py-2">
      <div>
        <div class="ui-type-heading text-right">
          <span class="mr-2 text-warning small" v-if="needPriceCorrection()">
            <font-awesome-icon icon="balance-scale" />
          </span>
          {{ getTotalPrice() }}
        </div>
      </div>
    </td>

    <td class="px-1 py-2 text-right">
      <CButton
        color="danger"
        variant="outline"
        type="button"
        @click.prevent="onRemove"
      >
        <font-awesome-icon icon="trash-alt" />
      </CButton>
    </td>
  </tr>
</template>

<script>
import get from "lodash/get";

import {
  addOrReplace,
  formatPrice,
  getOptionLabel,
  getTranslation,
} from "../../../helpers/common";
import { mapGetters } from "vuex";
import { getPriceUnitOptions, getPwOptions } from "../../../helpers/options";

export default {
  name: "MarketCartTableRow",

  props: {
    item: { type: Object, required: true },
    market: { type: Object, required: true },
  },

  data() {
    return {
      showProducts: false,
      showModal: false,
      quantity: 0,
    };
  },

  computed: {
    ...mapGetters("connections", ["currencySymbol"]),

    getProducerName() {
      return get(
        this.item,
        "view.mp.ppl.productproducer.producer.role.name",
        ""
      );
    },
    familyId() {
      return this.$store.state.connections.current.family.id;
    },

    productId() {
      return get(this.item, "view.mp.ppl.productproducer.id");
    },
    marketId() {
      return this.market.market.id;
    },
  },

  methods: {
    getTotalPrice() {
      const price = get(this.item, `view.tot`, null);
      return price !== null
        ? formatPrice(price, this.currencySymbol).format()
        : "";
    },

    needPriceCorrection() {
      return (
        get(this.item, "view.mp.ppl.pw", false) ||
        get(this.item, "view.mp.ppl.productproducer.pw", false)
      );
    },
    getPrice() {
      const price = get(this.item, `view.mp.ppm.price`, null);
      return price !== null
        ? formatPrice(price, this.currencySymbol).format()
        : "";
    },

    getPriceInfo() {
      const price_base_unit = get(this.item, `view.mp.price_base_unit`, null);
      if (price_base_unit) {
        const { price, unit } = price_base_unit;
        const unitOptions = getPriceUnitOptions(this.$i18n);
        return formatPrice(
          price,
          `${this.currencySymbol} / ${getOptionLabel(unit, unitOptions)}`
        ).format();
      }
      return "";
    },

    getProductName() {
      const translations = get(
        this.item,
        "view.mp.ppl.productproducer.translations"
      );
      return getTranslation(translations, this.$i18n.locale);
    },
    getVariantName() {
      const translations = get(this.item, "view.mp.ppl.translations");
      return getTranslation(translations, this.$i18n.locale);
    },
    getImage() {
      const variantImage = get(this.item, `view.mp.ppl.image.image_thumb`);
      if (variantImage) {
        return variantImage;
      }
      const productImage = get(
        this.item,
        `view.mp.ppl.productproducer.image.image_thumb`
      );
      if (productImage) {
        return productImage;
      }

      return "/loonity/img/placeholder.svg";
    },
    onChange(value) {
      const payload = {
        row: this.item,
        data: { marketId: this.marketId, quantity: value },
      };
      this.$root.$emit("onCartItemChangeQuantity", payload);
    },

    onSubmit() {
      const payload = {
        row: this.item,
        data: { marketId: this.marketId, quantity: this.quantity },
      };
      this.$root.$emit("onCartItemUpdate", payload);
    },

    onRemove() {
      this.showModal = true;
    },

    suffix() {
      const pp = get(this.item, "view.mp.ppl.productproducer");
      const pw_option = get(pp, "extra.pw_option", 1);
      return pw_option === 3
        ? `${getOptionLabel(pp.pw_unit, getPriceUnitOptions(this.$i18n))}`
        : `${getOptionLabel(pw_option, getPwOptions(this.$i18n), "short")}`;
    },

    step() {
      const isWeightProduct =
        get(this.item, "view.mp.ppl.productproducer.pw") === true;
      return isWeightProduct ? "0.1" : "1";
    },

    onCancel() {
      this.showModal = false;
    },

    onConfirm() {
      const payload = {
        row: this.item,
        data: { marketId: this.marketId, quantity: 0 },
      };
      this.$root.$emit("onCartItemRemove", payload);
      this.showModal = false;
    },

    availability() {
      const limitless = get(this.item, "view.mp.ppl.availability_limitless");
      if (limitless) return;

      const availability_xd = get(this.item, "view.mp.ppl.availability_xd");
      const availability = get(this.item, "view.mp.ppl.availability");
      const availability_start = get(
        this.data,
        "view.mp.ppl.availability_start"
      );

      return {
        availability: availability,
        availability_start: availability_start,
        availability_xd: availability_xd,
      };
    },
  },

  mounted() {
    this.quantity = this.item.qta;
  },
};
</script>
